import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const AuthLoadingPage = () => {
    const { user, pgUser, loading, authError, clearAuthError } = useAuth();
    const navigate = useNavigate();
    const [showRetryButton, setShowRetryButton] = useState(false);

    // Debug log on every render
    console.log('AuthLoadingPage render state:', { user, pgUser, loading });

    // Effect to handle retry button visibility
    useEffect(() => {
        if (authError?.type === 'server' || authError?.type === 'network') {
            const timer = setTimeout(() => {
                setShowRetryButton(true);
            }, 5000); // Show retry button after 5 seconds
            return () => clearTimeout(timer);
        }
    }, [authError]);

    // Main auth flow effect
    useEffect(() => {
        console.log('AuthLoadingPage useEffect triggered:', { user, pgUser, loading, authError });
        
        if (!loading) {
            if (authError) {
                // Don't navigate away if there's an error
                return;
            }
            
            if (user && pgUser) {
                console.log('Auth complete, checking user setup:', { 
                    setup_complete: pgUser.setup_complete,
                    email_confirmed: pgUser.email_confirmed 
                });
                
                if (!pgUser.setup_complete) {
                    console.log('Setup not complete, navigating to setup');
                    navigate('/setup');
                } else {
                    console.log('Setup complete, navigating to meal-plan');
                    navigate('/meal-plan');
                }
            } else if (!user) {
                // Check if we're in the email confirmation process
                const isEmailConfirmation = window.location.pathname === '/email-confirmation';
                if (!isEmailConfirmation) {
                    console.log('No user found and not in email confirmation, redirecting to login');
                    navigate('/login');
                } else {
                    console.log('No user but in email confirmation, waiting...');
                }
            } else {
                console.log('Waiting for auth state to settle:', { loading, user: !!user, pgUser: !!pgUser });
            }
        }
    }, [user, pgUser, loading, navigate, authError]);

    const getLoadingMessage = () => {
        if (!user) {
            return "Logging in...";
        }
        if (!pgUser) {
            return "Logging in...";
        }
        if (!pgUser.setup_complete) {
            return "Setting up your account...";
        }
        return "Loading your meal plan...";
    };

    const handleRetry = () => {
        clearAuthError();
        setShowRetryButton(false);
        window.location.reload();
    };

    return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
            <div className="text-center">
                {authError ? (
                    <>
                        <div className="alert alert-danger">
                            <h4>{authError.type === 'server' ? 'Server Error' : 
                                 authError.type === 'network' ? 'Network Error' : 
                                 'Authentication Error'}</h4>
                            <p>{authError.message}</p>
                            {showRetryButton && (
                                <button 
                                    className="btn btn-primary mt-3" 
                                    onClick={handleRetry}
                                >
                                    Retry
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div 
                            className="spinner-border" 
                            role="status"
                            style={{ color: '#52cd92' }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h3 className="mt-3">{getLoadingMessage()}</h3>
                        <div className="text-muted">
                            <small>
                                Status: {loading ? 'Loading...' : !user ? 'Waiting for user...' : !pgUser ? 'Loading user data...' : 'Complete'}
                            </small>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthLoadingPage;
